import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import { Box, Container, Fab, Input, InputAdornment, Tooltip, Typography } from "@mui/material";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { CaseOverviewTable } from "src/components/CaseOverviewTable";
import { PageHeader } from "src/components/page/PageHeader";
import { usePermissions } from "src/hooks";
import { useCases } from "src/hooks/useCases";
import { IsVisible } from "src/components/permissions";
import { CaseStatus } from "src/types";
import { useEffect, useState } from "react";

interface Props {}

export function CasesPage(props: Props) {
  const { t } = useTranslation();

  const [refetchInterval, setRefetchInterval] = useState<false | number>(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: cases } = useCases(refetchInterval);
  const { data: permissions } = usePermissions();

  /**
   * Effects
   */
  useEffect(updateRefreshInterval, [cases]);

  /**
   * Functions
   */
  function updateRefreshInterval() {
    const result = cases?.find((acase) => {
      if (acase.status === CaseStatus.PREPARING) return true;
      if (acase.status === CaseStatus.CALCULATING) return true;
      else return false;
    });

    // if any case is in pending status, increase refetch interval
    if (result) return setRefetchInterval(1000);
    else return setRefetchInterval(false);
  }

  function handleSearch(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    setSearchQuery(event.target.value);
  }

  return render();

  function render() {
    if (!cases) return null;

    return (
      <StyledContainer maxWidth="lg">
        {renderHeader()}
        <CaseOverviewTable className="cases-table" data={cases} searchQuery={searchQuery} />
      </StyledContainer>
    );
  }

  function renderHeader() {
    if (!permissions) return null;
    return (
      <PageHeader sx={{ gap: 2 }}>
        <Typography variant="h3" noWrap className="page-title">
          {t("title.cases")}
        </Typography>

        <Box display="flex" flexGrow={1} />

        {/* Purposely an uncontrolled component, no reflow for searchQuery necessary. Uncontrolled is thus faster */}
        <Input
          placeholder="Search by case name"
          type="search"
          autoFocus
          onChange={handleSearch}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
        />

        <IsVisible permission={[permissions.cases.createNormal, permissions.cases.createPredefined]} type="some">
          <Tooltip title={t<string>("tooltip.newCase")} placement="left">
            <Fab component={Link} to="/cases/new" className="button fab" color="primary" aria-label="add">
              <AddIcon />
            </Fab>
          </Tooltip>
        </IsVisible>
      </PageHeader>
    );
  }
}

const StyledContainer = styled(Container)`
  padding: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;

  .page-title {
    margin-bottom: ${({ theme }) => theme.spacing(3)};
  }

  .cases-table {
    height: 0;
    flex: 1 1 100px;
  }

  .button {
    & + .button {
      margin-left: ${({ theme }) => theme.spacing(1)};
    }
  }
`;
